import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Leaf, Calendar, Sprout, Globe, TreePine, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { ISOProgressionPath } from '../components/training/ISOProgressionPath';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';
import { standards } from '../data/standards/standardsData';

export const ISO14001LeadAuditor = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISO 14001 Lead Auditor Course",
    "description": "Get ISO 14001 Lead Auditor Course certification. Expert-led EMS auditor training for environmental & sustainability professionals.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy"
    },
    "coursePrerequisites": "Environmental management or auditing experience recommended",
    "educationalLevel": "Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "In-person",
      "duration": "5 days"
    }
  };

  const courseCompetencies = [
    'Lead EMS audit teams',
    'Evaluate environmental risks and impacts',
    'Ensure legal and regulatory compliance',
    'Support continuous environmental improvement initiatives'
  ];

  const targetAudience = [
    'Environmental Managers and Officers',
    'EMS Consultants and Advisors',
    'Internal and External Auditors',
    'Quality, Health, Safety & Environment (QHSE) professionals',
    'Sustainability and ESG professionals',
    'Management Representatives',
    'Graduates aspiring to become EMS auditors'
  ];

  const professionalBenefits = [
    {
      title: 'Internationally recognized auditor qualification',
      icon: Globe
    },
    {
      title: 'Career growth in auditing, consulting, and compliance roles',
      icon: TrendingUp
    },
    {
      title: 'Enhanced credibility as an EMS expert',
      icon: Award
    }
  ];

  const organizationalBenefits = [
    {
      title: 'Ability to conduct internal and supplier audits',
      icon: Users
    },
    {
      title: 'Improved environmental performance and compliance',
      icon: Target
    },
    {
      title: 'Reduced environmental risks and liabilities',
      icon: CheckCircle
    }
  ];

  const businessBenefits = [
    {
      title: 'Supports sustainability and ESG initiatives',
      icon: Sprout
    },
    {
      title: 'Enhances organizational reputation',
      icon: Award
    },
    {
      title: 'Meets customer and regulatory expectations',
      icon: CheckCircle
    }
  ];

  const careerOpportunities = [
    'ISO 14001 Lead Auditor',
    'EMS Consultant',
    'Sustainability Manager',
    'Environmental Compliance Officer',
    'Third-party Certification Auditor',
    'Corporate ESG Advisor'
  ];

  const industries = [
    'Manufacturing',
    'Energy',
    'Construction',
    'Pharmaceuticals',
    'Information Technology',
    'Logistics',
    'Government Sectors'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="ISO 14001 Lead Auditor Course | EMS Auditor Certification"
        description="Get ISO 14001 Lead Auditor Course certification. Expert-led EMS auditor training for environmental & sustainability professionals."
        keywords="ISO 14001 lead auditor, EMS auditor training, ISO 14001 certification, environmental management audit, ISO 14001:2015, sustainability auditor, EMS certification"
        canonical="/ISO-14001-Lead-Auditor-Course"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              <Leaf className="h-5 w-5" />
              ENVIRONMENTAL MANAGEMENT SYSTEMS
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              ISO 14001 Lead Auditor Course
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-8 max-w-4xl mx-auto leading-relaxed">
              Become a globally recognized ISO 14001 Lead Auditor. Master environmental management system audits and drive sustainability excellence across industries.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>5 Days Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-6 w-6" />
                <span>Global Recognition</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Back Button */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </section>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standards['iso-14001']} currentLevel="leadAuditor" />

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The ISO 14001 Lead Auditor Course is a globally recognized professional training program designed to develop competent auditors capable of assessing, implementing, and improving Environmental Management Systems (EMS) in accordance with ISO 14001:2015. As environmental compliance, sustainability, and ESG accountability become business priorities worldwide, certified ISO 14001 lead auditors are in high demand across industries.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This course equips professionals with in-depth auditing skills, practical knowledge of ISO 14001 requirements, and internationally accepted certification credentials.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is ISO 14001 Lead Auditor Course Section */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Is the ISO 14001 Lead Auditor Course?
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              The ISO 14001 Lead Auditor Course provides comprehensive training on conducting first-party, second-party, and third-party audits of Environmental Management Systems based on ISO 14001:2015 standards. The course follows ISO 19011 and ISO/IEC 17021 auditing guidelines, enabling participants to plan, conduct, report, and follow up on EMS audits effectively.
            </p>
          </motion.div>

          {/* Competencies Grid */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full border-2 border-green-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Successful participants gain the competence to:
                  </h3>
                  <div className="space-y-3">
                    {courseCompetencies.map((competency, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{competency}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-2xl mb-4">
                  <TreePine className="h-16 w-16 text-white" />
                </div>
                <p className="text-lg font-semibold text-gray-800">
                  Certified Environmental<br />Management Auditor
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Attend Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who Should Attend the ISO 14001 Lead Auditor Course?
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This course is ideal for professionals involved in environmental management, compliance, and auditing roles, including:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="h-full border-l-4 border-green-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Leaf className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800">{role}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Benefits of ISO 14001 Lead Auditor Course
            </h3>
          </motion.div>

          {/* Professional Benefits */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Professional Benefits
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              {professionalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-emerald-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-green-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Organizational Benefits */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Organizational Benefits
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              {organizationalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-emerald-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-green-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Business & Market Benefits */}
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Business & Market Benefits
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              {businessBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-emerald-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-green-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Opportunities Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Career Opportunities After ISO 14001 Lead Auditor Course
            </h4>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Completing the ISO 14001 Lead Auditor Course opens doors to multiple career paths, such as:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {careerOpportunities.map((career, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="h-full bg-gradient-to-br from-green-50 to-white border-2 border-green-200 hover:shadow-lg transition-all">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                      <Award className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900">{career}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Industries Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white"
          >
            <h5 className="text-2xl font-bold mb-4 text-center">
              Industries Hiring ISO 14001 Lead Auditors
            </h5>
            <div className="flex flex-wrap justify-center gap-4">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full font-medium"
                >
                  {industry}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-14001" />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Become an ISO 14001 Lead Auditor?
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Join environmental professionals worldwide who have advanced their careers with our comprehensive ISO 14001 Lead Auditor certification
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-white text-green-700 hover:bg-green-50 font-semibold px-8 py-6 text-lg"
              >
                Enroll Now
              </Button>
              <Button
                onClick={() => navigate('/training-calendar')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-green-700 font-semibold px-8 py-6 text-lg"
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
